<template>
  <div
    class="location"
    @click="openLocation"
    :data-location-scroll-anchor="location._id"
    :class="{
      'is-clickable': clickable,
      'has-highlight-color': highlight,
      'is-highlighted': highlighted
    }"
  >
    <div class="location__label" v-if="label">
      <LocationLabel :location="location" />
    </div>
    <div v-if="clickable" class="location__title">
      <router-link :to="route(location)" @click.native.stop="() => {}">
        {{ location.title }}
      </router-link>
    </div>
    <div v-else class="location__title">{{ location.title }}</div>
    <div class="location__address">
      <template v-if="location.district">
        {{ location.address | trim }},
        {{ location.district | district }}
        {{ location.district | city }}
      </template>
      <template v-else>
        {{ location.address }}
      </template>
    </div>
    <div class="location__seasons" v-if="seasons.length">
      <p>Saison</p>
      <ul>
        <li v-for="season in seasons" :key="season._id">{{ season.title }}</li>
      </ul>
    </div>
    <ul class="location__counts" v-if="productionCount">
      <li>
        {{ productionCount | countStr('Produktion', 'Produktionen') }}
      </li>
    </ul>
    <div
      v-if="images && (location.images || []).length"
      class="location__images"
    >
      <figure v-for="image in location.images" :key="image._key">
        <ResponsiveImage :image="image" :ratio="false" />
      </figure>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { filterByLocationId, filterByIds } from '@/util/filters'
import LocationLabel from '@/components/LocationLabel'
import ResponsiveImage from '@/components/ResponsiveImage.vue'

export default {
  name: 'LocationInfo',
  props: {
    location: {
      type: Object,
      required: true
    },
    label: {
      type: String,
      default: ''
    },
    images: {
      type: Boolean,
      default: false
    },
    highlight: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: true
    }
  },
  components: { LocationLabel, ResponsiveImage },
  computed: {
    ...mapGetters(['isLocationHighlighted', 'allProductions', 'allSeasons']),
    highlighted() {
      return this.isLocationHighlighted(this.location)
    },
    productions() {
      return filterByLocationId(this.allProductions, this.location._id)
    },
    productionCount() {
      return this.location.productionCount || this.productions.length
    },
    seasons() {
      const seasonIds = this.productions.map((p) => p.seasonId)
      return filterByIds(this.allSeasons, seasonIds)
    }
  },
  methods: {
    openLocation() {
      if (!this.clickable) return
      this.$router.push(this.route(this.location))
    }
  }
}
</script>

<style scoped>
.location {
  color: var(--color-text);
  &.is-clickable {
    cursor: pointer;
  }
}
.location__title {
  @mixin type-largest;
}
.location__address {
  @mixin type-larger;
}
.location.has-highlight-color {
  .location__title,
  .location__address {
    color: var(--color-highlight);
  }
}
.location__seasons {
  @mixin type-smaller;
  p,
  ul,
  li {
    display: inline;
  }
  p {
    padding-right: 0.3em;
  }
  li {
    &::after {
      content: ', ';
    }
    &:last-child::after {
      content: none;
    }
  }
}
.location__counts {
  @mixin type-smaller;
  font-weight: 600;
}
.location__images {
  flex: 0 0 100%;
  display: flex;
  flex-wrap: wrap;
  padding-top: 0.5em;
  padding-left: var(--content-indent);
  figure {
    margin-right: 3px;
  }
}
/deep/ .location__images {
  img {
    height: 40px;
    width: auto;
    width: calc(40px * var(--aspect-ratio, 1));
    object-fit: cover;
  }
}
@media only print {
  .location {
    display: inline-block;
    width: 100%;
    page-break-inside: avoid;
  }
}
</style>
